import { useContext, useEffect, useMemo, useRef } from 'react'

import { RecurlyPayment } from 'api'
import { useRecurly } from 'lib/recurly'
import { useCheckout, useUpdate } from 'features/checkout/hooks'
import { useUser } from 'hooks/use-user'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { cn } from 'utils/classMerge'

import { RecurlyContext } from '../../providers/index'
import { CHECKOUT_SUBMIT_TYPE } from '../form/consts'
import './styles.css'

const GOOGLE_ELEMENT_ID = 'google-pay-container'

interface GooglePayCallbacks {
  onPaymentAuthorized: (paymentData: { recurlyToken: RecurlyPayment }) => void
}

interface GooglePayConfig {
  googleMerchantId: string
  billingAddressRequired: boolean
  currency: string
  country: string
  total: string
  callbacks: GooglePayCallbacks
  buttonOptions: {
    buttonType: string
    buttonSizeMode: string
  }
}

export const GooglePay = ({
  className,
  source,
  checkoutSuccessSource,
}: {
  className?: string
  source?: string
  checkoutSuccessSource: string
}) => {
  const context = useContext(RecurlyContext)
  if (!context) {
    throw new Error('useRecurly must be used within a RecurlyContainer')
  }

  const { currentSubscription, setIsLoading, tier, interval, submitType } =
    context
  const { handleCheckout } = useCheckout()
  const { handleUpdate } = useUpdate()
  const user = useUser()
  const recurly = useRecurly()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const googlePayButtonRef = useRef<HTMLElement | null>(null)

  const onPaymentAuthorized = (paymentData: {
    recurlyToken: RecurlyPayment
  }) => {
    setIsLoading(true)
    const handlePaymentCallBack =
      submitType === CHECKOUT_SUBMIT_TYPE.update ? handleUpdate : handleCheckout
    handlePaymentCallBack({
      token: paymentData.recurlyToken,
      paymentMethod: MIXPANEL_PROPS.BUTTON_NAME.values.GOOGLEPAY,
      checkoutSuccessSource,
    })
  }

  const googlePayConfig: GooglePayConfig | null = useMemo(() => {
    const googleMerchantId = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID
    if (
      !currentSubscription?.currency ||
      !user?.countryCode ||
      !googleMerchantId
    )
      return null

    return {
      googleMerchantId,
      billingAddressRequired: true,
      currency: currentSubscription.currency,
      country: user.countryCode,
      total:
        submitType === CHECKOUT_SUBMIT_TYPE.update
          ? '0'
          : currentSubscription.unitPrice?.replace(/\D/g, '') || '0',
      callbacks: {
        onPaymentAuthorized,
      },
      buttonOptions: {
        buttonType: 'plain',
        buttonSizeMode: 'fill',
      },
    }
  }, [
    currentSubscription?.currency,
    currentSubscription?.unitPrice,
    currentSubscription?.planLabel,
    user?.countryCode,
    submitType,
  ])

  const onClick = () => {
    Mixpanel.trackPaymentButtonClicked({
      tier,
      planInterval: interval,
      buttonName: MIXPANEL_PROPS.BUTTON_NAME.values.GOOGLEPAY,
      source,
    })
  }

  useEffect(() => {
    if (!googlePayConfig || !containerRef.current) return

    const googlePay = recurly.GooglePay(googlePayConfig)

    const handleButtonClick = () => setIsLoading(true)

    const cleanup = () => {
      if (googlePayButtonRef.current && containerRef.current) {
        googlePayButtonRef.current.removeEventListener(
          'click',
          handleButtonClick,
        )
        containerRef.current.removeChild(googlePayButtonRef.current)
        googlePayButtonRef.current = null
      }
    }

    googlePay.on('ready', (button: HTMLElement) => {
      if (!containerRef.current) return

      if (!googlePayButtonRef.current) {
        googlePayButtonRef.current = button
        googlePayButtonRef.current.classList.add('custom-google-pay')
        googlePayButtonRef.current.addEventListener('click', handleButtonClick)
        containerRef.current.appendChild(googlePayButtonRef.current)
      }
    })

    googlePay.on('error', (err: unknown) => {
      setIsLoading(false)
      console.error('Google Pay error:', err)
    })

    return cleanup
  }, [googlePayConfig, setIsLoading])

  return (
    <div
      id={GOOGLE_ELEMENT_ID}
      ref={containerRef}
      className={cn('tw-w-full', className)}
      aria-label="Google Pay Button"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick()
        }
      }}
    />
  )
}
